import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plug, RefreshCw, HelpCircle } from 'lucide-react';
import { HelpTooltip } from '../components/HelpTooltip';
import Layout from '../components/Layout';
import api from '../utils/api';

const PLATFORM_HELP = [
  [/local services|lsa/i, 'Local Services Ads — the "Google Guaranteed" spots at the very top of Google. You pay per LEAD (a real call), not per click.'],
  [/google ads/i, 'Google Ads puts you at the top when someone searches "plumber near me". Connect it so clicks and spend flow into your scoreboard automatically.'],
  [/meta|facebook|instagram/i, 'Meta = Facebook & Instagram ads. Gets you in front of homeowners in your area before they even start searching.'],
  [/tag manager|gtm/i, 'Google Tag Manager — a tiny snippet on your pages that tracks which ad made the phone ring. Set it once, forget it.'],
  [/servicetitan|jobber|housecall/i, 'Your booking system. Connect it and booked jobs count themselves — no manual entry.'],
];
const platformHelp = (name) => (PLATFORM_HELP.find(([rx]) => rx.test(name || '')) || [])[1];

const STATUS_BADGES = {
  connected: { label: 'Connected', cls: 'bg-lime/15 text-lime border-lime/40' },
  not_connected: { label: 'Not Connected', cls: 'bg-gray-500/15 text-gray-300 border-gray-500/40' },
  needs_reconnect: { label: 'Needs Reconnect', cls: 'bg-orange/15 text-orange border-orange/40' },
  coming_soon: { label: 'Coming Soon', cls: 'bg-gray-500/10 text-gray-500 border-gray-600/40' },
};

export default function ConnectionsPage() {
  const [data, setData] = useState(null);
  const [unattr, setUnattr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);

  useEffect(() => {
    api.get('/connections/overview').then((r) => setData(r.data)).catch(console.error);
    api.get('/tracking/unattributed-summary').then((r) => setUnattr(r.data)).catch(console.error);
  }, []);

  const refresh = async () => {
    setSyncing(true);
    try { const { data: d } = await api.post('/sync/refresh'); setSyncResults(d); }
    catch (e) { console.error(e); } finally { setSyncing(false); }
  };

  if (!data) return <Layout variant="light"><div className="p-10 text-light-gray">Loading…</div></Layout>;

  return (
    <Layout variant="light">
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="connections-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black text-white uppercase">Connections</h1>
            <p className="text-light-gray mt-2 max-w-2xl">
              Connect the tools you already use so results flow into your campaigns automatically. {data.note}
            </p>
          </div>
          <button onClick={refresh} disabled={syncing} data-testid="sync-refresh-btn"
            className="px-4 py-2.5 rounded-lg bg-black border border-lime/40 text-lime font-bold text-sm hover:bg-lime/10 disabled:opacity-50 flex items-center gap-2">
            <RefreshCw size={15} className={syncing ? 'animate-spin' : ''} /> Refresh Sync
          </button>
        </div>

        {syncResults && (
          <div className="mb-6 p-4 rounded-xl bg-card-bg border border-divider" data-testid="sync-results">
            {syncResults.results.map((r) => (
              <p key={r.platform} className={`text-sm mb-1 ${r.ok ? 'text-light-gray' : 'text-gray'}`}>
                {r.ok ? '✓' : '·'} {r.message}
              </p>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {data.platforms.map((p) => {
            const badge = STATUS_BADGES[p.status] || STATUS_BADGES.coming_soon;
            return (
              <div key={p.id} data-testid={`connection-card-${p.id}`}
                className={`bg-card-bg border border-divider rounded-xl p-5 ${p.status === 'coming_soon' ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-black rounded-lg text-orange"><Plug size={16} /></div>
                    <div>
                      <p className="text-white font-bold leading-tight flex items-center gap-1.5">{p.name}
                        {platformHelp(p.name) && <HelpTooltip size={13} text={platformHelp(p.name)} testId={`help-platform-${p.id}`} />}
                      </p>
                      <p className="text-[13px] text-muted uppercase tracking-wide">{p.category}</p>
                    </div>
                  </div>
                  <span data-testid={`connection-status-${p.id}`}
                    className={`text-[10px] font-black px-2 py-1 rounded-full border shrink-0 ${badge.cls}`}>{badge.label}</span>
                </div>
                <p className="text-xs text-light-gray leading-relaxed mb-3">{p.description}</p>
                {p.last_sync_label && <p className="text-[13px] text-lime mb-2" data-testid={`connection-sync-${p.id}`}>{p.last_sync_label}</p>}
                {p.can_connect && p.status === 'not_connected' && (
                  <Link to="/integrations" data-testid={`connection-connect-${p.id}`}
                    className="inline-block text-xs font-bold text-black bg-lime rounded-lg px-3 py-1.5 hover:bg-lime/90">
                    Connect →
                  </Link>
                )}
                {p.status === 'needs_reconnect' && (
                  <Link to="/integrations" className="inline-block text-xs font-bold text-black bg-orange rounded-lg px-3 py-1.5 hover:bg-orange/90">
                    Reconnect →
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {/* Unattributed bucket */}
        <div className="mt-10 bg-card-bg border border-divider rounded-xl p-6" data-testid="unattributed-bucket">
          <h2 className="text-white font-bold text-xl flex items-center gap-2 mb-1">
            <HelpCircle size={18} className="text-orange" /> Unattributed Results
          </h2>
          {unattr ? (
            <>
              <p className="text-sm text-gray mb-4">{unattr.explainer}</p>
              <div className="flex gap-8 flex-wrap mb-4">
                <div>
                  <p className="text-3xl font-black text-white" data-testid="unattributed-count">{unattr.count}</p>
                  <p className="text-xs text-muted uppercase tracking-wide">Unattributed events</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-white" data-testid="unattributed-revenue">${unattr.revenue.toLocaleString()}</p>
                  <p className="text-xs text-muted uppercase tracking-wide">Unattributed revenue</p>
                </div>
              </div>
              {unattr.events.length > 0 ? (
                <div className="space-y-1.5" data-testid="unattributed-events">
                  {unattr.events.slice(0, 10).map((e) => (
                    <div key={e.id} className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-black border border-divider">
                      <span className="text-light-gray">{e.provider} event — no tracking code found</span>
                      <span className="text-gray">{new Date(e.received_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-lime">Nothing unattributed — every result so far carried a tracking code. 👌</p>
              )}
              <p className="text-xs text-gray mt-4">
                Seeing results here? Make sure your booking notes and ad links use the campaign's tracking code — the{' '}
                <Link to="/start-here" className="text-orange underline">Tracking &amp; Your Numbers lesson</Link> shows exactly where each one goes.
              </p>
            </>
          ) : <p className="text-sm text-gray">Loading…</p>}
        </div>
      </div>
    </Layout>
  );
}
